import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/authContext';
import { asOfficeDocType } from '../contexts/OfficeDocumentDrawerContext';
import { documentsApi } from '../lib/api/documentsApi.connect';
/**
 * Lists the current user's standalone Office documents (shirohige) so the
 * Artifacts tab can surface them alongside chat artifacts. shirohige lists
 * tenant-wide, so we owner-scope client-side to match the owner-scoped chat
 * artifacts — a user sees their own Office docs, not the whole tenant's.
 */ export function useOfficeArtifacts() {
    const { user } = useAuth();
    const query = useQuery({
        queryKey: [
            'office-artifacts',
            user?.id
        ],
        // Every folder, not just root: the Artifacts tab surfaces all of the user's
        // Office documents wherever they live, matching what chat can resume.
        queryFn: ()=>documentsApi.listDocuments({
                pageSize: 200,
                allFolders: true
            }),
        enabled: !!user,
        staleTime: 30000
    });
    const officeDocs = (query.data?.documents ?? [])// Owner-scope, and hide empty chat shells: create-office-document mints an
    // empty template (createdWithContent=false) at version 1; content arrives
    // when the user accepts a write-to-document fill, which commits version 2+.
    // So show a doc once it either carried real content at creation (an upload)
    // or has been filled since (versionCount > 1) — the remaining hidden case is
    // exactly the unfilled chat shell, which would open blank.
    .filter((d)=>d.ownerUserId === user?.id && (d.createdWithContent || d.versionCount > 1)).flatMap((d)=>{
        const docType = asOfficeDocType(d.documentType);
        if (!docType) return [];
        return [
            {
                id: d.id,
                title: d.name,
                docType,
                createdAt: new Date(d.createdAt),
                updatedAt: new Date(d.updatedAt)
            }
        ];
    });
    return {
        officeDocs,
        loading: query.isLoading,
        refetch: query.refetch
    };
}
